import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, MoveRight } from "lucide-react";
import { ClientMarquee } from "@/components/ClientMarquee";
import { Reveal } from "@/components/Reveal";
import { HOME } from "@/constants/testIds";
import { INDUSTRIES, SNAPSHOTS, SERVICES } from "@/data/content";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/53238dfc-9902-4045-ab2f-6fcec5697290/images/db6f112ec4c0103c39f5f2290b9b90e3826f5dd9e8d9f6ab51a02642cff72f61.png";

const HOME_STATS = [
    { value: "$60M+", label: "Media budget managed" },
    { value: "39", label: "Markets activated" },
    { value: "21 yrs", label: "Combined financial services focus" },
    { value: "188+", label: "Campaigns launched" }
];

export default function Home() {
    return (
        <div className="relative">
            {/* HERO */}
            <section
                data-testid={HOME.heroSection}
                className="relative min-h-[85vh] flex flex-col justify-center items-start overflow-hidden grain"
            >
                <div
                    className="absolute inset-0 -z-10"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(5,8,13,0.55) 0%, rgba(5,8,13,0.65) 50%, rgba(5,8,13,1) 100%), url(${HERO_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                    }}
                />
                <div className="container-x w-full py-20">
                    <Reveal>
                        <h1
                            className="font-display leading-[0.95] tracking-tight text-brand-mist max-w-[1280px] overflow-visible pr-2"
                            style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                        >
                            Marketing built for{" "}
                            <span className="text-gradient-blue italic inline-block pr-[0.12em] -mr-[0.02em]">forex brokers</span>{" "}
                            and financial services in Africa
                        </h1>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <Reveal className="lg:col-span-7" delay={120}>
                            <p className="text-lg sm:text-xl text-brand-frost/90 max-w-2xl leading-relaxed">
                                <span className="text-brand-primary">Adx Fusion</span> grows financial brands across Africa. Forex brokers and financial services firms hire us to turn ad spend into funded accounts in markets most agencies get wrong.
                            </p>
                        </Reveal>
                        <Reveal className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end" delay={240}>
                            <Link to="/contact" data-testid={HOME.heroCta} className="btn-primary">
                                Start a project <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/snapshot" data-testid={HOME.heroSecondary} className="btn-secondary">
                                See the work <MoveRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>

                <div className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 text-brand-primary/60">
                    <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center mt-12">Scroll</span>
                    <ArrowDownRight className="w-4 h-4 animate-bounce" />
                </div>
            </section>

            {/* MARQUEE, clients */}
            <section data-testid={HOME.marquee} className="py-12 border-y border-brand-primary/10 bg-brand-bg">
                <div className="container-x mb-6">
                    <p className="overline">Brands we&apos;ve worked with</p>
                </div>
                <ClientMarquee />
                <div className="mt-6">
                    <ClientMarquee outline speed="slow" />
                </div>
            </section>

            {/* STATS */}
            <section data-testid={HOME.statsSection} className="py-24 lg:py-32">
                <div className="container-x">
                    <Reveal className="mb-12">
                        <p className="overline">Built on industry experience</p>
                    </Reveal>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 border-t border-brand-primary/10 pt-16">
                        {HOME_STATS.map((s, i) => (
                            <Reveal key={s.label} delay={i * 80} className="flex flex-col gap-3">
                                <span className="font-mono text-xs text-brand-primary/60">0{i + 1}</span>
                                <p className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-mist tracking-tighter break-words">{s.value}</p>
                                <p className="text-sm text-brand-primary/80 max-w-[220px]">{s.label}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section data-testid={HOME.servicesSection} className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 items-end">
                        <Reveal className="lg:col-span-7">
                            <p className="overline mb-6">What we do</p>
                            <h2 className="font-display h-display-lg">
                                A full stack for <span className="italic text-brand-primary">growth</span> in financial markets.
                            </h2>
                        </Reveal>
                        <Reveal className="lg:col-span-5" delay={120}>
                            <p className="text-lg text-brand-frost/80 leading-relaxed">
                                Twelve tightly integrated practice areas. One senior team. Designed to be the only growth partner a financial services brand needs.
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                        {SERVICES.map((s, i) => {
                            const num = String(i + 1).padStart(2, "0");
                            return (
                                <Reveal
                                    key={s.slug}
                                    delay={(i % 4) * 60}
                                    data-testid={HOME.serviceCard(s.slug)}
                                    className="group relative border border-brand-primary/10 -ml-px -mt-px p-6 sm:p-8 lg:p-10 bg-brand-surface hover:bg-brand-elevated transition-colors duration-500 min-h-[220px] flex flex-col"
                                >
                                    <div className="flex items-start justify-between mb-10">
                                        <span className="font-mono text-xs text-brand-primary/60 tracking-widest">{num}</span>
                                        <ArrowUpRight className="w-5 h-5 text-brand-primary/60 transition-all duration-500 group-hover:text-brand-primary group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    </div>
                                    <h3 className="font-display text-2xl lg:text-[26px] leading-tight tracking-tight text-brand-mist mt-auto">{s.title}</h3>
                                </Reveal>
                            );
                        })}
                    </div>

                    <div className="mt-12 flex justify-end">
                        <Link to="/what-we-do" className="btn-secondary">
                            Explore capabilities <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section data-testid={HOME.industriesSection} className="py-24 lg:py-32 border-t border-brand-primary/10 bg-brand-surface/40">
                <div className="container-x">
                    <Reveal className="mb-20">
                        <p className="overline mb-6">Verticals</p>
                        <h2 className="font-display h-display-lg max-w-4xl">
                            Three industries. <span className="italic text-brand-primary">One obsession.</span>
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {INDUSTRIES.map((ind, i) => (
                            <Reveal
                                key={ind.slug}
                                delay={i * 120}
                                className="relative border-t border-brand-primary/20 pt-8"
                            >
                                <p className="overline mb-6">{ind.kicker}</p>
                                <h3 className="font-display h-display-sm text-brand-mist mb-4">{ind.title}</h3>
                                <p className="text-brand-frost/80 leading-relaxed">{ind.copy}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SNAPSHOT PREVIEW */}
            <section data-testid={HOME.snapshotPreview} className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x">
                    <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
                        <Reveal>
                            <p className="overline mb-6">Snapshot · selected work</p>
                            <h2 className="font-display h-display-lg">
                                Work that
                                <br />
                                <span className="italic text-brand-primary">moved the line.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={120}>
                            <Link to="/snapshot" className="btn-secondary">
                                View snapshot <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                        {SNAPSHOTS.slice(0, 4).map((s, i) => {
                            const colClass = i === 0
                                ? "lg:col-span-7"
                                : i === 1
                                ? "lg:col-span-5"
                                : i === 2
                                ? "lg:col-span-5"
                                : "lg:col-span-7";
                            return (
                                <Reveal key={s.slug} delay={i * 80} className={`${colClass}`}>
                                    <Link to="/snapshot" className="group block relative border border-brand-primary/10 bg-brand-surface h-full">
                                        <div className="relative overflow-hidden">
                                            <div
                                                className="aspect-[16/10] w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                                style={{ backgroundImage: `url(${s.image})` }}
                                            />
                                            <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-brand-bg/95 via-brand-bg/30 to-transparent" />
                                        </div>
                                        <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-end lg:absolute lg:inset-0">
                                            <p className="overline mb-3">{s.tag}</p>
                                            <h3 className="font-display h-display-sm text-brand-mist mb-2">{s.title}</h3>
                                            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                                                <span className="text-brand-frost/80 text-sm">{s.client}</span>
                                                <span className="font-mono text-xs text-brand-primary uppercase tracking-widest">{s.metric}</span>
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* BIG CTA */}
            <section data-testid={HOME.ctaSection} className="py-32 lg:py-40 border-t border-brand-primary/10 relative grain">
                <div className="container-x text-center">
                    <Reveal>
                        <p className="overline mb-8">Next chapter</p>
                        <h2 className="font-display h-display-lg" style={{ fontSize: "clamp(2.5rem, 8vw, 8rem)" }}>
                            Let&apos;s make your
                            <br />
                            <span className="text-gradient-blue italic">numbers move.</span>
                        </h2>
                        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                            <Link to="/contact" className="btn-primary">
                                Start a project <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/what-we-do" className="btn-secondary">
                                Our capabilities <MoveRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
